import { useState, useEffect, useRef } from 'react';
import { apiFetch } from './config';

// The tag FIELD — the same gesture RelationField established for drawing a
// relation (owner ruling 2026-08-04: "the typing option is far and away the
// best"), applied to the other thing you attach by hand.
//
// You TYPE the tag you mean. Your own most-used tags surface with no query at
// all, so the common case is one keystroke and Enter; a name that matches
// nothing offers to MINT it, which keeps the tag namespace as open as the
// relation namespace. GET /api/tags answers the empty field with the viewer's
// OWN vocabulary only — a personal list, not a popularity chart — and searches
// every tag once you type.
//
// It replaced a fixed chip row. A fixed set cannot cover an open namespace,
// which is the same reason the relation grid of sentence buttons was rejected;
// the names that row carried are passed in as `suggest` and seed the empty-query
// list until the viewer's own usage outranks them.
//
// A tag edge is born INERT and stays inert — no author auto-vote. `tagged` is
// structural, so no lens reads it and a vote there would assert nothing. That
// is deliberately UNLIKE RelationField, where the drawn edge is an epistemic
// reading and carries the author's +1.

export default function TagField({
  tags = [], // applied, as [{ key, name }]
  onAdd, // (name) => Promise
  onRemove, // (tag) => Promise
  suggest = [], // seed names for the empty-query list
  placeholder = 'tag it — type a name, Enter to attach…',
  autoFocus = false,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // late responses for a stale query must never override the current list — a
  // merge keyed by sequence, not a cancellation flag (the RelationField rule)
  const seq = useRef(0);
  const inputRef = useRef(null);

  const applied = new Set(tags.map((t) => t.name.toLowerCase()));

  useEffect(() => {
    if (!open) return undefined;
    const mySeq = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: '10' });
        if (query.trim()) params.set('q', query.trim());
        const res = await apiFetch(`/api/tags?${params.toString()}`);
        if (!res.ok || mySeq !== seq.current) return;
        const rows = await res.json();
        if (mySeq !== seq.current) return;
        setMatches(Array.isArray(rows) ? rows : []);
        setHighlight(0);
      } catch {
        /* a failed fetch leaves the previous list standing */
      }
    }, query.trim() ? 180 : 0);
    return () => clearTimeout(t);
  }, [query, open]);

  const typed = query.trim().toLowerCase().replace(/\s+/g, ' ');

  // Existing tags first, then the seeded names that are not already in the
  // list, then the offer to mint. A fully-matched name suppresses the mint
  // offer — the same rule the relation field follows, so you can never be
  // offered a duplicate of the thing directly above it.
  const fromGraph = matches
    .filter((m) => !applied.has(m.content.toLowerCase()))
    .map((m) => ({ name: m.content, uses: m.uses, mine: m.mine }));
  const graphNames = new Set(fromGraph.map((o) => o.name.toLowerCase()));
  const fromSuggest = suggest
    .filter(
      (name) =>
        !applied.has(name.toLowerCase()) &&
        !graphNames.has(name.toLowerCase()) &&
        (!typed || name.toLowerCase().includes(typed))
    )
    .map((name) => ({ name, suggested: true }));
  const options = [...fromGraph, ...fromSuggest];
  const exact = options.some((o) => o.name.toLowerCase() === typed) || applied.has(typed);
  if (typed && !exact) options.push({ name: typed, create: true });

  const commit = async (opt) => {
    if (!opt || busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(opt.name);
      setQuery('');
      setMatches([]);
      // stay open: attaching several tags in a row is the normal case
      setHighlight(0);
      inputRef.current?.focus();
    } catch (err) {
      setError(err.message || 'Could not attach that tag');
    } finally {
      setBusy(false);
    }
  };

  const drop = async (tag) => {
    if (busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      await onRemove(tag);
    } catch (err) {
      setError(err.message || 'Could not remove that tag');
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' || e.key === ',') {
      if (!options.length) return;
      e.preventDefault();
      commit(options[Math.min(highlight, options.length - 1)]);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && tags.length) {
      e.preventDefault();
      drop(tags[tags.length - 1]);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--wv-sp-2)', flex: 1, minWidth: 0 }}>
      {tags.map((tag) => (
        <span key={tag.key} className="wv-chip wv-tint" style={{ gap: 'var(--wv-sp-1)' }}>
          {tag.name}
          <button
            type="button"
            onClick={() => drop(tag)}
            disabled={busy || disabled}
            aria-label={`Remove the tag ${tag.name}`}
            title={`Remove the tag ${tag.name}`}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              minWidth: 0,
              minHeight: 0,
              lineHeight: 1,
              color: 'var(--wv-muted)',
            }}
          >
            ×
          </button>
        </span>
      ))}

      <div style={{ position: 'relative', flex: 1, minWidth: 'min(200px, 100%)' }}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          autoFocus={autoFocus}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Tags"
          style={{
            width: '100%',
            padding: 'var(--wv-sp-1) var(--wv-sp-2)',
            boxSizing: 'border-box',
            fontSize: 'var(--wv-fs-ui)',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--wv-text)',
          }}
        />
        {open && options.length > 0 && (
          <div className="wv-dropdown" role="listbox">
            {options.map((opt, i) => (
              <div
                key={opt.create ? '__create' : `${opt.name}|${opt.suggested ? 's' : 'g'}`}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(opt)}
                className={i === highlight ? 'wv-dropdown-item wv-tint' : 'wv-dropdown-item'}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  gap: 'var(--wv-sp-2)',
                  fontStyle: opt.create ? 'italic' : 'normal',
                }}
              >
                {opt.create ? (
                  <span>mint “{opt.name}” — a new tag</span>
                ) : (
                  <>
                    <span>{opt.name}</span>
                    {opt.suggested ? (
                      <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                        suggested
                      </span>
                    ) : (
                      <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                        {opt.mine ? `you: ${opt.mine}` : `${opt.uses} use${opt.uses === 1 ? '' : 's'}`}
                      </span>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        {error && (
          <div style={{ color: 'var(--wv-neg)', fontSize: 'var(--wv-fs-sm)', marginTop: 'var(--wv-sp-1)' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
